import { Divider, Form } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";

import "./PushNotificationSetting.css";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import Spinner from "components/Spinner/Spinner";
import TextInput from "components/Inputs/TextInput/TextInput";
import Selector from "components/Selector/Selector";
import centralApi from "services/centralApi";
import useMessage from "hooks/useMessage";
import { getToken } from "redux/features/app/authTokenSlice";
import { STRING_BOOLEAN_OPTIONS } from "utils/constants/selectorOptions";
import { DBPlatformEnum } from "utils/enums";

interface PushNotificationSettingProps {
  currentAppKey: any;
  updateCurrentAppKey: () => void;
}

interface RootState {
  loginUser: {
    data: {
      api_key: string;
    };
  };
  activeApp: {
    appId: string;
  };
}

const PushNotificationSetting: React.FC<PushNotificationSettingProps> = ({
  currentAppKey,
  updateCurrentAppKey,
}) => {
  const { showSuccess, showError } = useMessage();
  const [platform, setPlatform] = useState<string>(DBPlatformEnum.ANDROID);
  const [form] = Form.useForm();
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState(false);

  const api_key = useSelector((state: RootState) => state.loginUser.data.api_key);
  const appId = useSelector((state: RootState) => state.activeApp.appId);
  const authToken = useSelector((state: any) => state.authToken.token);

  const onFormSubmit = async(values: any) => {
    const { hl, dl, edl, message, getAppDropdown, pushDismiss } = values;

    const payload = {
      args: JSON.stringify({
        hl,
        dl,
        edl,
        message,

        getAppDropdown,
        pushDismiss,
        app_id: appId,
        platform,

      }),
      app_id: appId,
      api_key,
      authToken,
    };

    await savePushCharLimit(payload);
    dispatch(getToken());
  };

  async function savePushCharLimit(formData: any) {
    const url: string = `/i/apps/savePushCharLimit`;

    try {
      setLoading(true);
      await centralApi("POST", url, formData, null);
      showSuccess(t("sucessfullySavedLabel"));

      setLoading(false);
    } catch (error) {
      setLoading(false);
      showError(t("somethingWrongLabel"));

    }
    updateCurrentAppKey();

  }

  useEffect(() => {
    if (!currentAppKey || !currentAppKey[0]) return;

    const charLimits = currentAppKey[0]?.pushNotificationCharLimit?.[platform] || {};

    form.setFieldsValue({
      hl: charLimits.hl || "",
      dl: charLimits.dl || "",
      edl: charLimits.edl || "",
      message: currentAppKey[0]?.pushNotificationCharLimit?.message || "",
      getAppDropdown: currentAppKey[0]?.getAppEnabled,
      pushDismiss: currentAppKey[0]?.pushDismiss
    });
  }, [currentAppKey, platform, form]);

  return (
    <div className="push-notification-setting-container">
      <div className="mt-4 mb-4  ">
        <h6 className="push-notification-setting-container-heading">
          {t("pushNotificationSettingLabel")}
        </h6>
        <Divider className="push-notification-setting-custom-divider" />

        <div className="mt-4 align-items-center push-notification-setting-form">
          <div className="mb-4">
            <p>{t("pushNotificationSettingDescriptionLabel")}</p>

            {loading ? (
              <Spinner />
            ) : (
              <Form form={form} layout="horizontal" onFinish={onFormSubmit}>
                <Form.Item label={t("platformLabel")}>
                  <Selector
                    value={platform}
                    onChange={(val) => setPlatform(val)}
                    options={[
                      { label: t("androidLabel"), value: DBPlatformEnum.ANDROID },
                      { label: t("iosLabel"), value: DBPlatformEnum.IOS },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                      label={t("headerLimitLabel")}
                      name={"hl"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <TextInput />
                    </Form.Item>
                    <Form.Item
                      label={t("descriptionLimit")}
                      name={"dl"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <TextInput />
                    </Form.Item>
                    <Form.Item
                      label={t("expandedDescriptionList")}
                      name={"edl"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <TextInput />
                    </Form.Item>
                    <Form.Item
                      label={t("messageLimitLabel")}
                      name={"message"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <TextInput />
                    </Form.Item>
                  
                    <Form.Item
                      label={t("getAppEnablementLabel")}
                      name={"getAppDropdown"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <Selector
                        options={[
                          {
                            label: t("enableLabel"),
                            value: STRING_BOOLEAN_OPTIONS.TRUE,
                          },
                          {
                            label: t("disableLabel"),
                            value: STRING_BOOLEAN_OPTIONS.FALSE,
                          },
                        ]}
                        parseStringBoolean={true}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("pushDismissLabel")}
                      name={"pushDismiss"}
                      rules={[
                        {
                          required: true,
                          message: t("plzInputLabel") as string,
                        },
                      ]}
                    >
                      <Selector
                        options={[
                          {
                            label: t("enableLabel"),
                            value: STRING_BOOLEAN_OPTIONS.TRUE,
                          },
                          {
                            label: t("disableLabel"),
                            value: STRING_BOOLEAN_OPTIONS.FALSE,
                          },
                          
                        ]}
                        parseStringBoolean={true}
                      />
                    </Form.Item>

                    <div className="">
                      <PrimaryButton
                        size="middle"
                        type="primary"
                        htmlType="submit"
                        className="pns-btn-save"
                      >
                        {t("saveLabel")}
                      </PrimaryButton>
                    </div>

              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSetting;
